import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from './ui/Button';
import { Spinner } from './ui/Spinner';
import { sendOAuthRequest } from '../utils/AuthService';
import { isSandboxAutoSignIn } from '../api/General/SandboxDemo';
import { useTranslation } from 'react-i18next';
import Logo from '../assets/images/logo.png';

const LandingPage = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);

    const handleLogin = useCallback(() => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setLoading(true);
        // sendOAuthRequest likely performs a redirect; loading state prevents double click
        try {
            // CO-917: come back to the URL that showed this page (e.g. /settings/notifications from an email).
            sendOAuthRequest(`${window.location.pathname}${window.location.search}${window.location.hash}`);
        } finally {
            // If redirect doesn't happen (e.g., error), re-enable after short delay
            setTimeout(() => {
                loadingRef.current = false;
                setLoading(false);
            }, 4000);
        }
    }, []);

    // Sandbox demo: the server auto-signs every visitor in, so start the sign-in redirect without a click.
    // The status endpoint answers false on every other deployment, leaving this page unchanged there.
    useEffect(() => {
        let cancelled = false;
        isSandboxAutoSignIn().then((auto) => {
            if (!auto || cancelled) return;
            // `?lang=ar|en|fr` (e.g. from the Arabic website) survives the OIDC round-trip as a short-lived cookie
            // that the sandbox login page reads; without it the server falls back to Accept-Language.
            const lang = new URLSearchParams(window.location.search).get('lang')?.toLowerCase();
            if (lang === 'ar' || lang === 'en' || lang === 'fr') {
                document.cookie = `mowazaf_demo_lang=${lang}; path=/; max-age=600; SameSite=Lax; Secure`;
            }
            handleLogin();
        });
        return () => {
            cancelled = true;
        };
    }, [handleLogin]);

    return (
        <div className="auth-container">
            <div className="auth-card" role="main" aria-labelledby="landing-auth-title">
                <div className="auth-form-section">
                    <img src={Logo} alt="Mowazaf Logo" className="auth-logo" loading="lazy" />
                    <h1 id="landing-auth-title" className="auth-title">{t('Welcome to Mowazaf')}</h1>
                    <p className="auth-subtitle">{t('Please sign in to access your account')}</p>
                    <Button
                        variant="default"
                        onClick={handleLogin}
                        disabled={loading}
                        className="auth-btn w-full"
                        aria-label={t('Sign In') as string}
                    >
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                {t('Signing In')}...
                            </>
                        ) : (
                            t('Sign In')
                        )}
                    </Button>
                    <p className="auth-alt">
                        {t('New to Mowazaf?')}<a href="/signup">{t('Create a free workspace')}</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LandingPage;
